import { useState, useCallback, useEffect } from 'react';
import { Droplets, Check, Info, Clock } from 'lucide-react';
import LabBench from '../../LabBench';
import { Dropper } from '@/components/lab';
import InfoBox from '../../InfoBox';

// Educational content for each step
const stepInfoContent: Record<number, {
  title: string;
  content: string;
  whatHappens: string;
  observe: string[];
  equation?: string;
}> = {
  1: {
    title: 'Preparing the Aluminum Solution',
    content: 'Aluminum(III) solutions contain Al³⁺ ions, which are colorless in solution. Unlike transition metals, aluminum has no d electrons available for d-d transitions, so its solutions do not absorb visible light.',
    whatHappens: 'You are adding 3 mL of Al³⁺ solution to a test tube. The solution is colorless because aluminum ions do not absorb visible light.',
    observe: [
      'Solution is completely colorless',
      'Solution should be clear, not cloudy',
      'Al³⁺ has no characteristic color'
    ]
  },
  2: {
    title: 'Adjusting pH to ~6',
    content: 'The aluminon test works best at pH around 6, which is slightly acidic. At this pH, aluminum ions are soluble but the aluminon dye can still form a stable complex. Too high or too low pH interferes with the test.',
    whatHappens: 'You adjust the pH to approximately 6 using HCl or NH₄OH. This ensures optimal conditions for the aluminon lake formation.',
    observe: [
      'Solution remains colorless after pH adjustment',
      'pH paper should show ~6 (slightly acidic)',
      'No precipitate should form at this pH'
    ]
  },
  3: {
    title: 'Placing the Drop on Filter Paper',
    content: 'The spot test is performed on filter paper because it concentrates the sample and provides a white background against which the red lake color can be easily seen. The paper also helps separate the colored complex from unreacted reagents during the wash step.',
    whatHappens: 'A single drop of the aluminum solution is placed on the filter paper. The solution spreads into a small circular spot as it absorbs into the paper fibers.',
    observe: [
      'Drop spreads into a circular spot',
      'Spot may appear slightly wet',
      'No color change yet - solution is colorless'
    ]
  },
  4: {
    title: 'Adding Aluminon Reagent',
    content: 'Aluminon (triammonium salt of aurintricarboxylic acid) is an organic dye that forms a red "lake" complex with aluminum ions. A lake is a pigment formed when a soluble dye precipitates with a metal ion, creating an insoluble colored compound.',
    whatHappens: 'Adding aluminon to the aluminum spot begins the formation of the aluminum-aluminon lake. The spot may turn orange-pink as the complex begins to form.',
    observe: [
      'Spot color changes to orange or pink',
      'Color is from aluminon dye + aluminum',
      'The lake complex is beginning to form'
    ],
    equation: 'Al³⁺ + Aluminon → Al-Aluminon lake (red)'
  },
  5: {
    title: 'Waiting for the Reaction',
    content: 'The formation of the aluminum-aluminon lake requires time for the complex to fully develop. During this 30-second period, the aluminon molecules coordinate with aluminum ions, creating the characteristic red pigment.',
    whatHappens: 'You wait 30 seconds for the aluminum-aluminon lake to fully form. The color may intensify during this time as more complex is created.',
    observe: [
      'Color gradually intensifies',
      'Lake complex is forming',
      'Reaction needs time to complete'
    ]
  },
  6: {
    title: 'Rinsing the Filter Paper',
    content: 'Rinsing with distilled water removes excess, unreacted aluminon from the paper. If aluminum is present, the red lake remains because it is insoluble. If aluminum is absent, the color washes away because no lake has formed.',
    whatHappens: 'Water is applied to wash the filter paper. Excess aluminon (which would also be colored) is removed, leaving only the insoluble aluminum-aluminon lake if aluminum is present.',
    observe: [
      'Water removes excess reagent',
      'Non-bound color washes away',
      'Bound lake color persists if Al³⁺ present'
    ]
  },
  7: {
    title: 'Observing the Remaining Spot',
    content: 'After rinsing, only the insoluble aluminum-aluminon lake should remain if aluminum is present. The characteristic bright red spot that persists after washing is the positive test result. If the color washes away completely, aluminum is absent.',
    whatHappens: 'You observe whether a bright red spot remains on the filter paper after rinsing. A persistent red spot confirms aluminum; no color indicates absence.',
    observe: [
      'Bright red spot remains if Al³⁺ present',
      'Color is from insoluble lake complex',
      'Complete color removal = negative test'
    ]
  },
  8: {
    title: 'Recording the Positive Result',
    content: 'The persistence of a bright red spot after washing is a POSITIVE confirmatory test for aluminum ions. The aluminon test is selective for aluminum, though iron(III) can interfere. The test is widely used in qualitative analysis.',
    whatHappens: 'You record that the test is positive for Al³⁺. The persistent red lake is definitive evidence for the presence of aluminum ions.',
    observe: [
      'Positive result: red spot confirms Al³⁺',
      'Lake is insoluble - remains after washing',
      'Record observations in your lab notebook'
    ],
    equation: 'Al³⁺ + Aluminon → Al-Aluminon lake (persistent red)'
  }
};

interface Part5AluminumTestProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

export default function Part5AluminumTest({ onStepComplete, currentStep = 1 }: Part5AluminumTestProps) {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [alSolutionAdded, setAlSolutionAdded] = useState(false);
  const [pHAdjusted, setPHAdjusted] = useState(false);
  const [dropOnPaper, setDropOnPaper] = useState(false);
  const [aluminonAdded, setAluminonAdded] = useState(false);
  const [waitProgress, setWaitProgress] = useState(0);
  const [waited, setWaited] = useState(false);
  const [rinsed, setRinsed] = useState(false);
  const [spotObserved, setSpotObserved] = useState(false);
  const [resultRecorded, setResultRecorded] = useState(false);

  // Colors for the spot test
  const getSpotColor = () => {
    if (!dropOnPaper) return '#F5F5F5';
    if (!aluminonAdded) return '#F5F5F5';
    if (!rinsed) return '#FF8A65'; // Orange before rinse
    return '#F44336'; // Bright red after rinse (aluminum-aluminon lake)
  };

  // Wait timer effect
  useEffect(() => {
    if (aluminonAdded && !waited && waitProgress > 0 && waitProgress < 100) {
      const interval = setInterval(() => {
        setWaitProgress(prev => {
          const newProgress = prev + 3.33;
          if (newProgress >= 100) {
            setWaited(true);
            if (onStepComplete) onStepComplete('aluminum-5');
          }
          return Math.min(newProgress, 100);
        });
      }, 100);
      return () => clearInterval(interval);
    }
  }, [aluminonAdded, waited, waitProgress, onStepComplete]);

  const handleAddAlSolution = useCallback(() => {
    setAlSolutionAdded(true);
    if (onStepComplete) onStepComplete('aluminum-1');
  }, [onStepComplete]);

  const handleAdjustpH = useCallback(() => {
    if (alSolutionAdded) {
      setPHAdjusted(true);
      if (onStepComplete) onStepComplete('aluminum-2');
    }
  }, [alSolutionAdded, onStepComplete]);

  const handlePlaceDrop = useCallback(() => {
    if (pHAdjusted) {
      setDropOnPaper(true);
      if (onStepComplete) onStepComplete('aluminum-3');
    }
  }, [pHAdjusted, onStepComplete]);

  const handleAddAluminon = useCallback(() => {
    if (dropOnPaper) {
      setAluminonAdded(true);
      setWaitProgress(1);
      if (onStepComplete) onStepComplete('aluminum-4');
    }
  }, [dropOnPaper, onStepComplete]);

  const handleRinse = useCallback(() => {
    if (waited) {
      setRinsed(true);
      if (onStepComplete) onStepComplete('aluminum-6');
    }
  }, [waited, onStepComplete]);

  const handleObserveSpot = useCallback(() => {
    if (rinsed) {
      setSpotObserved(true);
      if (onStepComplete) onStepComplete('aluminum-7');
    }
  }, [rinsed, onStepComplete]);

  const handleRecordResult = useCallback(() => {
    if (spotObserved) {
      setResultRecorded(true);
      if (onStepComplete) onStepComplete('aluminum-8');
    }
  }, [spotObserved, onStepComplete]);

  return (
    <LabBench
      experimentName="Aluminum Spot Test"
      completedCount={[alSolutionAdded, pHAdjusted, dropOnPaper, aluminonAdded, waited, rinsed, spotObserved, resultRecorded].filter(Boolean).length}
      totalSteps={8}
      equipment={['Al³⁺ solution', 'pH paper', 'Filter paper', 'Aluminon solution', 'Wash bottle']}
      labMode="guided"
      currentStep={currentStep}
    >
      <div className="aluminum-test">
        {/* Equipment Area */}
        <div className="equipment-row">
          {/* Test Tube with Al³⁺ solution */}
          <div className="equipment-section">
            <svg viewBox="0 0 50 110" width={45} height={100}>
              <defs>
                <linearGradient id="tube-glass-al" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                  <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                  <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                </linearGradient>
              </defs>

              {/* Test tube rim */}
              <ellipse cx="25" cy="10" rx="12" ry="4" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Test tube body */}
              <path d="M13 10 L13 85 Q13 97 25 97 Q37 97 37 85 L37 10" fill="url(#tube-glass-al)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Liquid fill - colorless */}
              {alSolutionAdded && (
                <path
                  d="M15 45 L15 83 Q15 94 25 94 Q35 94 35 83 L35 45 Z"
                  fill="#F5F5F5"
                  opacity="0.7"
                />
              )}

              {/* Glass shine */}
              <line x1="16" y1="15" x2="16" y2="80" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span className="equipment-label">Al³⁺ Solution</span>
          </div>

          {/* Filter Paper with spot */}
          <div className="equipment-section filter-paper-section">
            <svg viewBox="0 0 100 100" width={100} height={100}>
              <defs>
                <filter id="paper-texture">
                  <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="5" result="noise" />
                  <feDiffuseLighting in="noise" lightingColor="#FFF8E1" surfaceScale="2" result="light">
                    <feDistantLight azimuth="45" elevation="60" />
                  </feDiffuseLighting>
                  <feComposite in="SourceGraphic" in2="light" operator="arithmetic" k1="1" k2="0" k3="0" k4="0" />
                </filter>
                <radialGradient id="spot-gradient" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor={getSpotColor()} />
                  <stop offset="70%" stopColor={getSpotColor()} stopOpacity="0.7" />
                  <stop offset="100%" stopColor={getSpotColor()} stopOpacity="0" />
                </radialGradient>
              </defs>

              {/* Filter paper circle */}
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="#FFF8E1"
                stroke="#D4C8A8"
                strokeWidth="1"
              />

              {/* Paper texture lines */}
              <g opacity="0.2">
                <line x1="20" y1="30" x2="80" y2="30" stroke="#C4B896" strokeWidth="0.5" />
                <line x1="15" y1="50" x2="85" y2="50" stroke="#C4B896" strokeWidth="0.5" />
                <line x1="20" y1="70" x2="80" y2="70" stroke="#C4B896" strokeWidth="0.5" />
              </g>

              {/* Wet spot from solution */}
              {dropOnPaper && (
                <circle
                  cx="50"
                  cy="50"
                  r={aluminonAdded ? 20 : 15}
                  fill="url(#spot-gradient)"
                  opacity={rinsed ? 1 : 0.8}
                >
                  {aluminonAdded && !rinsed && (
                    <animate
                      attributeName="r"
                      values="15;20;15"
                      dur="2s"
                      repeatCount="indefinite"
                    />
                  )}
                </circle>
              )}

              {/* Bright red spot after rinse */}
              {rinsed && (
                <circle
                  cx="50"
                  cy="50"
                  r="18"
                  fill="#F44336"
                  opacity="0.9"
                >
                  <animate
                    attributeName="opacity"
                    values="0.7;0.9;0.7"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}
            </svg>
            <span className="equipment-label">Filter Paper</span>
          </div>

          {/* Aluminon Dropper */}
          <div className="equipment-section">
            <Dropper
              label="Aluminon"
              liquidColor="#FF7043"
              onClick={dropOnPaper && !aluminonAdded ? handleAddAluminon : undefined}
              active={dropOnPaper && !aluminonAdded}
              disabled={!dropOnPaper || aluminonAdded}
            />
          </div>

          {/* Wash Bottle */}
          <div className="equipment-section">
            <svg viewBox="0 0 50 120" width={45} height={110}>
              <defs>
                <linearGradient id="bottle-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(200,220,240,0.8)" />
                  <stop offset="50%" stopColor="rgba(255,255,255,0.5)" />
                  <stop offset="100%" stopColor="rgba(200,220,240,0.7)" />
                </linearGradient>
              </defs>

              {/* Bottle body */}
              <path d="M10 40 Q10 30 15 25 L15 15 L35 15 L35 25 Q40 30 40 40 L40 100 Q40 105 35 105 L15 105 Q10 105 10 100 Z"
                fill="url(#bottle-grad)"
                stroke="#7A9BB8"
                strokeWidth="1.5"
              />

              {/* Spout tube */}
              <path d="M25 15 Q25 5 35 0 L37 0 Q27 7 27 15"
                fill="none"
                stroke="#7A9BB8"
                strokeWidth="2"
              />

              {/* Water inside */}
              <rect x="12" y="45" width="26" height="55" fill="#B3E5FC" opacity="0.6" rx="2" />

              {/* Cap */}
              <rect x="15" y="10" width="20" height="6" fill="#FF5722" rx="2" />

              {/* Wash label */}
              <text x="25" y="80" fontSize="6" fill="#666" textAnchor="middle">H2O</text>
            </svg>
            <span className="equipment-label">Wash Bottle</span>
          </div>
        </div>

        {/* Status Messages */}
        <div className="status-section">
          {!alSolutionAdded && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 1</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(1); }}
                >
                  ?
                </button>
              </div>
              <p>Add 3 mL of <strong>Al³⁺ solution</strong> to a test tube.</p>
              <button className="action-btn" onClick={handleAddAlSolution}>
                <Droplets size={16} />
                Add Al³⁺ Solution
              </button>
            </div>
          )}

          {alSolutionAdded && !pHAdjusted && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 2</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(2); }}
                >
                  ?
                </button>
              </div>
              <p>Adjust pH to <strong>approximately 6</strong> using HCl or NH₄OH.</p>
              <button className="action-btn" onClick={handleAdjustpH}>
                <Check size={16} />
                Adjust pH to ~6
              </button>
            </div>
          )}

          {pHAdjusted && !dropOnPaper && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 3</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(3); }}
                >
                  ?
                </button>
              </div>
              <p>Place <strong>one drop</strong> of solution on filter paper.</p>
              <button className="action-btn" onClick={handlePlaceDrop}>
                <Droplets size={16} />
                Place Drop on Paper
              </button>
            </div>
          )}

          {dropOnPaper && !aluminonAdded && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 4</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(4); }}
                >
                  ?
                </button>
              </div>
              <p>Add <strong>one drop of aluminon</strong> solution to the spot.</p>
              <p className="hint">Click the aluminon dropper.</p>
            </div>
          )}

          {aluminonAdded && !waited && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 5</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(5); }}
                >
                  ?
                </button>
              </div>
              <p>Wait <strong>30 seconds</strong> for the reaction to occur.</p>
              <div className="wait-progress">
                <Clock size={16} />
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${waitProgress}%` }} />
                </div>
                <span>{Math.round(waitProgress * 0.3)}s / 30s</span>
              </div>
            </div>
          )}

          {waited && !rinsed && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 6</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(6); }}
                >
                  ?
                </button>
              </div>
              <p><strong>Rinse</strong> the filter paper with distilled water.</p>
              <button className="action-btn" onClick={handleRinse}>
                <Droplets size={16} />
                Rinse with Water
              </button>
            </div>
          )}

          {rinsed && !spotObserved && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 7</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(7); }}
                >
                  ?
                </button>
              </div>
              <p>Observe if the <strong>red spot remains</strong> after rinsing.</p>
              <button className="action-btn" onClick={handleObserveSpot}>
                <Check size={16} />
                Observe Spot
              </button>
            </div>
          )}

          {spotObserved && !resultRecorded && (
            <div className="instruction-card success">
              <div className="instruction-header">
                <span className="step-badge">Step 8</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(8); }}
                >
                  ?
                </button>
              </div>
              <p>Record the <strong>positive result</strong> for aluminum confirmation.</p>
              <button className="action-btn success" onClick={handleRecordResult}>
                <Check size={16} />
                Record Result
              </button>
            </div>
          )}

          {resultRecorded && (
            <div className="result-card">
              <Check size={20} />
              <div>
                <p><strong>Al³⁺ Confirmed!</strong></p>
                <p>The persistent bright red spot after rinsing confirms the aluminum-aluminon lake.</p>
              </div>
            </div>
          )}
        </div>

        {/* Spot color indicator */}
        {aluminonAdded && (
          <div className="spot-indicator">
            <span>Spot Color:</span>
            <div
              className="color-swatch"
              style={{ backgroundColor: getSpotColor() }}
            />
            <span className="color-label">
              {!rinsed ? 'Orange-pink (before rinse)' : 'Bright red (after rinse)'}
            </span>
          </div>
        )}

        {/* Theory Box */}
        <div className="theory-box">
          <h4><Info size={14} /> The Aluminon Spot Test</h4>
          <p>
            Aluminon (triammonium salt of aurintricarboxylic acid) forms a red "lake" with aluminum ions.
            A lake is a pigment formed when a dye is adsorbed onto an inorganic compound.
            The test is performed on filter paper at pH ~6.
          </p>
          <div className="equation">
            Al<sup>3+</sup> + Aluminon → Al-Aluminon lake (bright red)
          </div>
          <p className="equation-note">If aluminum is present, the red spot remains after washing; otherwise, it washes away.</p>
        </div>

        <style>{`
          .aluminum-test {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
            max-width: 650px;
          }

          .equipment-row {
            display: flex;
            gap: 30px;
            align-items: flex-end;
            flex-wrap: wrap;
            justify-content: center;
          }

          .equipment-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .filter-paper-section {
            padding: 10px;
            background: var(--bg-elevated);
            border-radius: 12px;
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .status-section {
            width: 100%;
            min-height: 100px;
          }

          .instruction-card {
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
            border-left: 4px solid var(--primary);
          }

          .instruction-card.success {
            border-left-color: #00A86B;
          }

          .step-badge {
            display: inline-block;
            background: var(--primary);
            color: white;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
            margin-bottom: 8px;
          }

          .instruction-card p {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-primary);
          }

          .instruction-card .hint {
            font-size: 12px;
            color: var(--text-muted);
            font-style: italic;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 20px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            margin-top: 8px;
          }

          .action-btn:hover {
            background: var(--primary-dark);
            transform: translateY(-2px);
          }

          .action-btn.success {
            background: #00A86B;
          }

          .action-btn.success:hover {
            background: #008B5B;
          }

          .result-card {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 16px 20px;
            background: rgba(0, 168, 107, 0.1);
            border-radius: 12px;
            color: #00A86B;
          }

          .result-card p {
            margin: 0;
            color: var(--text-primary);
          }

          .result-card p:first-child {
            color: #00A86B;
            font-weight: 600;
          }

          .wait-progress {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-top: 12px;
            color: var(--text-secondary);
          }

          .progress-bar {
            flex: 1;
            height: 8px;
            background: var(--bg-dark);
            border-radius: 4px;
            overflow: hidden;
          }

          .progress-fill {
            height: 100%;
            background: var(--primary);
            transition: width 0.1s;
          }

          .spot-indicator {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 16px;
            background: var(--bg-elevated);
            border-radius: 8px;
            font-size: 13px;
          }

          .color-swatch {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            border: 2px solid var(--border);
          }

          .color-label {
            color: var(--text-secondary);
          }

          .theory-box {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
          }

          .theory-box h4 {
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .theory-box p {
            margin: 0 0 12px 0;
            font-size: 12px;
            color: var(--text-primary);
            line-height: 1.6;
          }

          .theory-box .equation {
            text-align: center;
            font-size: 14px;
            font-family: 'Times New Roman', serif;
            color: var(--text-secondary);
            background: var(--bg-dark);
            padding: 10px;
            border-radius: 6px;
            margin-bottom: 8px;
          }

          .equation-note {
            font-size: 11px !important;
            color: var(--text-muted) !important;
            text-align: center;
          }

          .instruction-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 8px;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp2"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
    </LabBench>
  );
}
